import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { RecaptchaV2Widget } from '../RecaptchaV2Widget';
import { RecaptchaErrorCode, RecaptchaVersion } from '../recaptcha';
import { recaptchaMessages } from '../recaptcha-messages';
import { isRecaptchaError } from '../recaptcha-utils';
import { useRecaptchaFeature } from './useRecaptchaFeature';
import type { RecaptchaAction, RecaptchaFeature, RecaptchaPayload } from '../recaptcha';

const getReCaptchaToken = async (
    siteKey: string,
    action: RecaptchaAction,
): Promise<string | null> => {
    if (typeof window !== 'undefined' && window.grecaptcha?.enterprise) {
        return window.grecaptcha.enterprise.execute(siteKey, { action });
    }
    return null;
};

export enum RecaptchaWorkflowState {
    IDLE = 'IDLE',
    VERIFYING_V3 = 'VERIFYING_V3',
    V3_COMPLETE = 'V3_COMPLETE',
    V2_REQUIRED = 'V2_REQUIRED',
    VERIFYING_V2 = 'VERIFYING_V2',
    V2_COMPLETE = 'V2_COMPLETE',
    ERROR = 'ERROR',
}

export interface UseRecaptchaWorkflowOptions {
    feature: RecaptchaFeature;
    action: RecaptchaAction;
    autoSubmitOnV2?: boolean;
    onV2Complete?: () => void | Promise<void>;
}

export interface UseRecaptchaWorkflowResult {
    isRecaptchaEnabled: boolean;
    getRecaptchaPayload: () => Promise<RecaptchaPayload | undefined>;
    handleApiError: (error: unknown) => boolean;
    handleApiSuccess: () => void;
    V2Widget: ReactNode | null;
    errorMessage: string | null;
    state: RecaptchaWorkflowState;
    isBlocked: boolean; // True when button should be disabled
    resetWorkflow: () => void;
}

export const useRecaptchaWorkflow = ({
    feature,
    action,
    autoSubmitOnV2 = true,
    onV2Complete,
}: UseRecaptchaWorkflowOptions): UseRecaptchaWorkflowResult => {
    const intl = useIntl();
    const [state, setState] = useState<RecaptchaWorkflowState>(RecaptchaWorkflowState.IDLE);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [v2Token, setV2Token] = useState<string | null>(null);
    const v2WidgetRef = useRef<{ reset: () => void }>(null);

    const recaptchaFeature = useRecaptchaFeature(feature);

    const isBlocked =
        state === RecaptchaWorkflowState.VERIFYING_V3 ||
        state === RecaptchaWorkflowState.V2_REQUIRED;

    const resetWorkflow = useCallback(() => {
        setState(RecaptchaWorkflowState.IDLE);
        setErrorMessage(null);
        setV2Token(null);
        v2WidgetRef.current?.reset();
    }, []);

    useEffect(() => {
        if (autoSubmitOnV2 && state === RecaptchaWorkflowState.V2_COMPLETE && onV2Complete) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            onV2Complete();
        }
        // we don't need to re-run this effect when onV2Complete changes, since it is set on every rerender on the parent
        // so it triggers the use effect. The downside is that onV2Complete can't be dynamic.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoSubmitOnV2, state]);

    const getRecaptchaPayload = useCallback(async (): Promise<RecaptchaPayload | undefined> => {
        if (!recaptchaFeature.isEnabled || !recaptchaFeature.enterpriseConfig) {
            return undefined;
        }

        setErrorMessage(null);

        // If we have a V2 token, use it
        if (v2Token && state === RecaptchaWorkflowState.V2_COMPLETE) {
            return {
                token: v2Token,
                version: RecaptchaVersion.V2,
                action,
                feature,
            };
        }

        // Execute V3 reCAPTCHA
        setState(RecaptchaWorkflowState.VERIFYING_V3);

        try {
            const token = await getReCaptchaToken(
                recaptchaFeature.enterpriseConfig.siteKeyV3,
                action,
            );

            if (!token) {
                throw new Error('Failed to get reCAPTCHA V3 token');
            }

            const result: RecaptchaPayload = {
                token,
                version: RecaptchaVersion.V3,
                action,
                feature,
            };

            setState(RecaptchaWorkflowState.V3_COMPLETE);
            return result;
        } catch (error) {
            setState(RecaptchaWorkflowState.ERROR);
            setErrorMessage(intl.formatMessage(recaptchaMessages.verificationFailed));
            throw error;
        }
    }, [
        recaptchaFeature.isEnabled,
        recaptchaFeature.enterpriseConfig,
        v2Token,
        state,
        intl,
        action,
        feature,
    ]);

    const handleApiError = useCallback(
        (error: unknown): boolean => {
            if (!isRecaptchaError(error)) {
                return false;
            }

            const { errorCode } = error.body;

            switch (errorCode) {
                case RecaptchaErrorCode.V2_REQUIRED:
                    if (recaptchaFeature.config?.enableV2Fallback) {
                        setState(RecaptchaWorkflowState.V2_REQUIRED);
                        setErrorMessage(intl.formatMessage(recaptchaMessages.v2Required));
                        return true;
                    }
                    setState(RecaptchaWorkflowState.ERROR);
                    setErrorMessage(intl.formatMessage(recaptchaMessages.v2Disabled));
                    return true;

                case RecaptchaErrorCode.TOKEN_EXPIRED:
                    setState(RecaptchaWorkflowState.ERROR);
                    setErrorMessage(intl.formatMessage(recaptchaMessages.tokenExpired));
                    // Reset V2 widget if we're in V2 state
                    if (
                        state === RecaptchaWorkflowState.V2_REQUIRED ||
                        state === RecaptchaWorkflowState.VERIFYING_V2 ||
                        state === RecaptchaWorkflowState.V2_COMPLETE
                    ) {
                        v2WidgetRef.current?.reset();
                    }
                    return true;

                case RecaptchaErrorCode.ACTION_MISMATCH:
                    setState(RecaptchaWorkflowState.ERROR);
                    setErrorMessage(intl.formatMessage(recaptchaMessages.actionMismatch));
                    return true;

                case RecaptchaErrorCode.INVALID_TOKEN:
                    setState(RecaptchaWorkflowState.ERROR);
                    setErrorMessage(intl.formatMessage(recaptchaMessages.invalidToken));
                    return true;

                case RecaptchaErrorCode.HOSTNAME_MISMATCH:
                    setState(RecaptchaWorkflowState.ERROR);
                    setErrorMessage(intl.formatMessage(recaptchaMessages.hostnameMismatch));
                    return true;

                case RecaptchaErrorCode.UNSUPPORTED_VERSION:
                    setState(RecaptchaWorkflowState.ERROR);
                    setErrorMessage(intl.formatMessage(recaptchaMessages.unsupportedVersion));
                    return true;

                case RecaptchaErrorCode.V2_DISABLED:
                    setState(RecaptchaWorkflowState.ERROR);
                    setErrorMessage(intl.formatMessage(recaptchaMessages.v2Disabled));
                    return true;

                case RecaptchaErrorCode.VERIFICATION_FAILED:
                default:
                    setState(RecaptchaWorkflowState.ERROR);
                    setErrorMessage(intl.formatMessage(recaptchaMessages.verificationFailed));
                    return true;
            }
        },
        [recaptchaFeature.config, intl, state],
    );

    const handleApiSuccess = useCallback(() => {
        setState(RecaptchaWorkflowState.IDLE);
        setErrorMessage(null);
        setV2Token(null);
        v2WidgetRef.current?.reset();
    }, []);

    const handleV2Token = useCallback((token: string) => {
        setV2Token(token);
        setState(RecaptchaWorkflowState.V2_COMPLETE);
        setErrorMessage(null);
    }, []);

    const handleV2Expired = useCallback(() => {
        setV2Token(null);
        setState(RecaptchaWorkflowState.V2_REQUIRED);
        setErrorMessage(intl.formatMessage(recaptchaMessages.v2Expired));
    }, [intl]);

    const handleV2Error = useCallback(() => {
        setV2Token(null);
        setState(RecaptchaWorkflowState.V2_REQUIRED);
        setErrorMessage(intl.formatMessage(recaptchaMessages.v2Failed));
    }, [intl]);

    const V2Widget = useMemo((): ReactNode => {
        if (state !== RecaptchaWorkflowState.V2_REQUIRED || !recaptchaFeature.enterpriseConfig) {
            return null;
        }
        return React.createElement(RecaptchaV2Widget, {
            ref: v2WidgetRef,
            siteKey: recaptchaFeature.enterpriseConfig.siteKeyV2,
            onToken: handleV2Token,
            onExpired: handleV2Expired,
            onError: handleV2Error,
        });
    }, [state, recaptchaFeature.enterpriseConfig, handleV2Token, handleV2Expired, handleV2Error]);

    return {
        isRecaptchaEnabled: recaptchaFeature.isEnabled,
        getRecaptchaPayload,
        handleApiError,
        handleApiSuccess,
        V2Widget,
        errorMessage,
        state,
        isBlocked,
        resetWorkflow,
    };
};
